// Ported from exams primitives.tsx PageHeading (winner over libms: has the breadcrumb slot); renders the page's single h1, breadcrumb slot wrapped in a labeled <nav> landmark, font-heading dropped (utility not in the mini substrate).
import { Show, createMemo, type Component, type JSX } from 'solid-js';
import { CircleHelp } from 'lucide-solid';
import { IconCapsule, type CategoryHue } from '../category-hue/index.js';
import { HelpNote, type HelpTone } from '../help-note/index.js';
import { helpMode, toggleHelpMode } from '../help-mode/index.js';
import { IconButton } from '../icon-button/index.js';

/**
 * A page's own contextual help — the ONE help block a screen carries, rendered by
 * PageHeading directly under the heading row so every page explains itself in the
 * same place. Two to four sentences in the reader's words: what the page is for,
 * the one or two verbs the operator uses here, and what the figures or columns
 * mean where that is not obvious. `tone` defaults to `info`; `warning` is for a
 * page whose actions cannot be undone (delete, withdraw, write off).
 */
export interface PageHelp {
  /** A short title over the note — usually the page's name or its question ("What is a hold?"). */
  title?: string;
  /** The help text. Plain sentences or a short list; no grant ids, no table names. */
  children: JSX.Element;
  /** `info` (default) · `warning` for irreversible pages · `success` / `neutral` where the note is reassurance or reference. */
  tone?: HelpTone;
  /**
   * ALWAYS render the note, and show no "?" beside the title.
   *
   * For a page with NO SHELL AROUND IT — an invitation, a password reset, a code
   * sign-in, a scanned card: the header's HelpToggle lives in the app shell, and
   * these pages do not have one. A "?" there could only turn help ON, with
   * nothing on the page able to turn it off again, so a visitor who pressed it
   * would leave help mode latched for every later visit. Forcing the note is the
   * honest trade: a door is read once by somebody who has never seen the product,
   * and its guidance is not optional detail (HelpNote's own `force` duty —
   * onboarding-critical guidance).
   *
   * Never use it on a console screen: inside the shell the reader owns the gate.
   */
  force?: boolean;
}

/**
 * The page's single `<h1>` with its subtitle, breadcrumb and header actions.
 * Every routed screen opens with one — it is what a screen reader lands on and
 * what the browser tab is named after. Detail screens with a photo and status
 * use `@erp/ui/entity-profile-header` instead.
 *
 * A console's landing carries its module IDENTITY capsule beside the h1
 * (`icon` + `hue` from MODULE_HUE, DEV-GUIDE-002 §12.5) — the 40px IconCapsule
 * that fin, cms, audit and lib each used to hand-roll around this component.
 *
 * Every page HELPS (2026-09-22): `help` renders the page's HelpNote directly under
 * the heading row while contextual help is on. While it is off, a quiet "?"
 * beside the title turns it on — Spectrum's contextual-help duty (a quiet
 * ActionButton beside a heading), here wired to the shared help-mode gate rather
 * than a popover, so one press restores every hint on the page. The header's
 * HelpToggle stays the global switch; this is the per-page affordance beside it.
 * `help.force` is for a page with no shell around it (a door): the note is always
 * shown and the "?" is not offered, because there would be no toggle to undo it.
 */
export function PageHeading(props: {
  /** The h1 — the screen's name in sentence case ("All patrons"), or the record's name on a detail screen. */
  title: string;
  /** One line under the title saying what the screen is for, in the user's words — no implementation nouns. */
  subtitle?: string;
  /** A lucide-solid icon COMPONENT for the identity capsule beside the title; rendered only with `icon`, in `hue` (or neutral). */
  icon?: Component<{ size?: number; class?: string }>;
  /** The module's hue from `MODULE_HUE` for the capsule — identity, never status. Omitted with an `icon`, the capsule is neutral (an area the map has not assigned). */
  hue?: CategoryHue | 'neutral';
  /**
   * Header buttons, right-aligned. Only the verbs that are NOT the form's
   * save (that lives in StickyActionBar): "Add patron", "Export CSV", "Import".
   * One primary among them at most.
   */
  /**
   * A status mark that belongs to the RECORD, rendered beside the title rather
   * than among the verbs — "Available", "On loan", "Draft". Added 2026-10-01:
   * a detail screen had no slot for one, so its StatusBadge travelled in
   * `actions` and read as a fourth button, and on a phone it wrapped onto the
   * button row away from the name it describes. A badge states what the record
   * IS; the actions say what you may do to it.
   */
  badge?: JSX.Element;
  actions?: JSX.Element;
  /** Breadcrumb links above the title, wrapped in a labelled `<nav>` landmark — usually `<Breadcrumbs>`. */
  breadcrumb?: JSX.Element;
  /**
   * The page's contextual help — one block per page, rendered as a HelpNote under
   * the heading row while help mode is on. When help mode is OFF the title row shows
   * a quiet "?" (aria-label "Show help for this page") that turns it back on.
   * `{ title, children, tone }`; see `PageHelp`.
   */
  help?: PageHelp;
  /** Extra classes on the wrapper — margin adjustments only (`mb-0` inside a panel). */
  class?: string;
}) {
  // Memoised like EmptyState's `action`: the object carries JSX, so reading the
  // getter per field would mint fresh nodes for each of HelpNote's three reads.
  const help = createMemo(() => props.help);
  return (
    <div class={`mb-5 ${props.class ?? ''}`}>
      <Show when={props.breadcrumb}>
        <nav aria-label="Breadcrumb" class="mb-2 text-sm text-base-subtle">
          {props.breadcrumb}
        </nav>
      </Show>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex min-w-0 items-start gap-3">
          <Show when={props.icon}>
            {(icon) => (
              /* mt-0.5 seats the 40px capsule on the h1's cap line (28px text at
                 leading-tight), the offset every hand-rolled site had settled on. */
              <span class="mt-0.5 flex shrink-0">
                <IconCapsule hue={props.hue ?? 'neutral'} icon={icon()} size="l" />
              </span>
            )}
          </Show>
          <div class="min-w-0">
            {/* flex-wrap so a long name plus its badge can take two lines on a
                phone instead of squeezing the title. */}
            <div class="flex min-w-0 flex-wrap items-center gap-1">
              <h1 class="text-2xl leading-tight tracking-tight">{props.title}</h1>
              <Show when={props.badge}>
                <span class="ms-1.5 flex shrink-0">{props.badge}</span>
              </Show>
              {/* The per-page "?" — shown only while help is OFF (while it is on,
                  the note below IS the help, and the header's HelpToggle turns it
                  off). The 16px `s` glyph rides the 32px hit box; the h1 is 22px
                  at leading-tight (a 27.5px line), so `-my-1` folds the box's
                  extra height back into the title line instead of pushing the
                  subtitle down. */}
              <Show when={help() && !help()?.force && !helpMode()}>
                <IconButton
                  id="page-heading-help"
                  icon={CircleHelp}
                  label="Show help for this page"
                  size="s"
                  /* LEFTWARD, and this is a geometry fact, not a taste: the
                     bubble is a daisyUI ::before painted outside the button,
                     "Show help for this page" measures 172px on a 32px
                     trigger, and `tooltip-top` centres it. Measured on stage
                     at 390px, this control sat at x=342, so the centred bubble
                     reached x=444 and gave the shell's main 54px of sideways
                     scroll with nothing in the DOM crossing the edge to find.
                     Leftward it grows into the title, where there is room; in
                     the worst case (a wrapped title putting the ? at the left
                     margin) it is clipped, because negative-x overflow is not
                     scrollable in LTR. Fourth instance of this shape — see
                     @erp/ui/toolbar's quick actions for the first three. */
                  placement="left"
                  class="-my-1"
                  onClick={() => toggleHelpMode()}
                />
              </Show>
            </div>
            {/* THE SUBTITLE STANDS DOWN ON A PHONE (2026-10-01, measured on
                the titles register at 390px). It is explanatory prose — "Every
                title in the catalogue — search, filter and manage copies" —
                which wrapped to two lines AND held the title block at the full
                358px, so the header's verbs could never sit beside it and took
                a row of their own. Hiding it below `sm` took the heading from
                124px to 42px and lifted the first row of content from y=545 to
                463 on an 844px screen.

                Nothing is lost that the page does not say twice: the <h1> names
                the screen, every register prints its own `note` line under the
                filters, and the `?` beside the title opens the full contextual
                help. From `sm` the subtitle returns unchanged. */}
            <Show when={props.subtitle}>
              <p class="mt-1 hidden text-xs text-base-subtle sm:block">{props.subtitle}</p>
            </Show>
          </div>
        </div>
        <Show when={props.actions}>
          <div class="flex min-w-0 flex-wrap items-center gap-2">{props.actions}</div>
        </Show>
      </div>
      {/* The page's help, under the row — HelpNote gates itself on helpMode(). */}
      <Show when={help()}>
        {(h) => (
          <HelpNote tone={h().tone} title={h().title} force={h().force} class="mt-3">
            {h().children}
          </HelpNote>
        )}
      </Show>
    </div>
  );
}
